import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import {
  Box,
  Typography,
  Stack,
  Grid,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
} from '@mui/material';

import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import Divider from '@mui/material/Divider';
import { useGetMyPersonalization } from 'services/v1/Tenant/ConfigurationService';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { MyViewDashboardItem, QuickAccessItem } from 'types/api/Tenant/ConfigurationTypes';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { getQuickAccessNavigateURL } from 'components/LayoutComponent/VerticalLayout/QuickAccess';
import { PAGE_REF_NAVIGATION } from 'utils/Animation';
import { useNavigate } from 'react-router-dom';
import { useDashboardNaviagationPrefetchHandler } from 'hooks/useDashboardNavigationPrefetchHandler';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { PersonalUpdateCard, PersonalUpdateHeader } from './PersonalUpdateCard';
import { HomeSection, HomeSectionPanel, HomeSectionPlaceholder, HomeSectionTabs } from './HomeSectionTabs';
import { KPIDisplay } from './KPIDisplay';
import { DisplayAccouuntability } from './DisplayAccouuntability';
import { MyViewDashboardPreview } from './MyViewDashboardPreview';
import { MyViewHeader } from './MyViewHeader';

const listContainerStyle = {
  backgroundColor: '#fff',
  minWidth: 150,
  height: '100%',
  overflowY: 'auto',
  my: 1,
  px: 2,
};

const menuItemStyle = {
  py: 0,
  px: 0.5,
  borderRadius: '4px',
  color: '#797979',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    outline: '1px solid #aeafb0',
  },
  '&:active': {
    backgroundColor: '#42BB9320',
  },
  '&.Mui-selected': {
    backgroundColor: '#3f50b514',
    '& .MuiTypography-root': { fontWeight: 700, color: '#3f50b5' },
    '&:hover': { backgroundColor: '#3f50b51a' },
  },
};

const menuItemTextStyle = { fontSize: 15, py: 0.5 };

interface ComponentDefinitionView {
  section: HomeSection;
  componentDefinitionId: string;
  componentDefinitionName: string;
  selectedDashboard: MyViewDashboardItem | null;
}

// Rowena sends null for a section without a definition, but a definition saved
// without any text still arrives as bare tags such as <p></p>.
const hasRowenaText = (content?: string | null) =>
  Boolean(content && new DOMParser().parseFromString(content, 'text/html').body.textContent?.trim());

export default function AdminTenantHomePage() {
  const { role, profile } = useSelector((state: RootState) => state.auth);
  const { data: allMyViewsData, isLoading } = useGetMyPersonalization({ enabled: true });
  const [activeSolution, setActiveSolution] = useState<AutoCompleteItem | null>(null);
  const [selectedDashboard, setSelectedDashboard] = useState<MyViewDashboardItem | null>(null);
  const [isMyViewVisible, setIsMyViewVisible] = useState(false);
  const [selectedKpiId, setSelectedKpiId] = useState<string | null>(null);
  const [selectedAccountabilityId, setSelectedAccountabilityId] = useState<string | null>(null);
  const [activeHomeSection, setActiveHomeSection] = useState<HomeSection>('planning');
  const [componentDefinitionView, setComponentDefinitionView] = useState<ComponentDefinitionView | null>(null);
  const prefetchHandler = useDashboardNaviagationPrefetchHandler();
  const navigate = useNavigate();
  const welcomeContent = allMyViewsData?.data.welcomeContent;
  const welcomeMessage = allMyViewsData?.data.welcomeMessage;
  const dataInsightsContent = allMyViewsData?.data.dataInsightsContent;
  const planningContent = allMyViewsData?.data.planningContent;
  const hasWelcomeMessage = Boolean(welcomeContent?.trim() || welcomeMessage?.trim());
  const shouldShowMyViewOptions =
    activeHomeSection === 'tracking' &&
    !selectedAccountabilityId &&
    componentDefinitionView?.section !== 'tracking' &&
    (isMyViewVisible || (Boolean(allMyViewsData?.data) && !hasWelcomeMessage));

  const mapSolutionWithDashboard = useMemo(() => {
    const map = new Map();
    if (allMyViewsData?.data) {
      allMyViewsData.data.solutions.forEach((solution) => {
        const dashboards = allMyViewsData.data.dashboards.filter((dsb) => dsb.solutionDefinition.id === solution.value);
        map.set(solution.value.toString(), dashboards);
      });
    }

    return map;
  }, [allMyViewsData?.data]);
  const myViewDashboardList: MyViewDashboardItem[] = useMemo(() => {
    if (activeSolution?.value) {
      return mapSolutionWithDashboard.get(activeSolution.value) ?? [];
    }
    return [];
  }, [activeSolution, mapSolutionWithDashboard]);
  // Auto-select the first solution on load, and drop a selection that no longer exists
  useEffect(() => {
    const solutions = allMyViewsData?.data.solutions;
    if (!solutions?.length) return;
    const isActiveStillAvailable =
      activeSolution && solutions.some((solution) => solution.value === activeSolution.value);
    if (!isActiveStillAvailable) {
      setActiveSolution(solutions[0]);
    }
  }, [activeSolution, allMyViewsData?.data.solutions]);

  useEffect(() => {
    if (!isMyViewVisible) return;

    const frameId = window.requestAnimationFrame(() => {
      document.getElementById('my-view-options')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isMyViewVisible]);

  const handleOnSolutionSelect = (item: AutoCompleteItem) => {
    setActiveSolution(item);
    setSelectedDashboard((currentDashboard) =>
      String(currentDashboard?.solutionDefinition.id) === String(item.value) ? currentDashboard : null,
    );
  };

  const handleOnDashboardClick = (dashboardItem: MyViewDashboardItem) => {
    setSelectedDashboard(dashboardItem);
    handleOnPrefetchMyViewItem(dashboardItem);
  };

  const handleOnMyViewItemClick = (myViewItem: QuickAccessItem) => {
    const navigateURL = getQuickAccessNavigateURL(myViewItem, true, PAGE_REF_NAVIGATION.LEFT, role);
    navigateURL && navigate(navigateURL);
  };

  const handleOnMyViewItemPrefetch = (myViewItem: QuickAccessItem, nextMyViewItem?: QuickAccessItem) => {
    prefetchHandler(myViewItem, nextMyViewItem);
  };

  const handleOnPrefetchMyViewItem = (dashboardItem: MyViewDashboardItem) => {
    dashboardItem.myViews.forEach((myViewItem, index) => {
      const nextMyViewItem = dashboardItem.myViews?.[index + 1];
      prefetchHandler(myViewItem, nextMyViewItem);
    });
  };

  // KPI, accountability and explore links in any section's Rowena content
  // open their target in Tracking, where those displays live.
  const handleWelcomeKpiSelect = (kpiDefinitionId: string) => {
    setActiveHomeSection('tracking');
    setComponentDefinitionView(null);
    setIsMyViewVisible(false);
    setSelectedAccountabilityId(null);
    setSelectedKpiId(kpiDefinitionId);
    window.requestAnimationFrame(() => {
      document.getElementById('kpi-display')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleWelcomeAccountabilitySelect = (accountabilityDefinitionId: string) => {
    setActiveHomeSection('tracking');
    setComponentDefinitionView(null);
    setIsMyViewVisible(false);
    setSelectedAccountabilityId(accountabilityDefinitionId);
    window.requestAnimationFrame(() => {
      document.getElementById('accountability-display')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleWelcomeExplore = () => {
    setActiveHomeSection('tracking');
    setComponentDefinitionView(null);
    setSelectedAccountabilityId(null);
    setIsMyViewVisible(true);
  };

  // A Component Definition link shows the selected definition and its dashboards
  // right under the card holding the link, which is always in the active section.
  const handleWelcomeComponentDefinitionSelect = (componentDefinitionId: string, componentDefinitionName: string) => {
    const firstDashboard: MyViewDashboardItem | undefined = mapSolutionWithDashboard.get(componentDefinitionId)?.[0];
    if (firstDashboard) handleOnPrefetchMyViewItem(firstDashboard);
    setComponentDefinitionView({
      section: activeHomeSection,
      componentDefinitionId,
      componentDefinitionName,
      selectedDashboard: firstDashboard ?? null,
    });
  };

  const handleOnComponentDefinitionDashboardClick = (dashboardItem: MyViewDashboardItem) => {
    setComponentDefinitionView((view) => view && { ...view, selectedDashboard: dashboardItem });
    handleOnPrefetchMyViewItem(dashboardItem);
  };

  const renderDashboardColumns = (
    dashboards: MyViewDashboardItem[],
    activeDashboard: MyViewDashboardItem | null,
    onDashboardClick: (dashboardItem: MyViewDashboardItem) => void,
  ) => (
    <>
      <Stack
        width={{ xs: '100%', lg: '22%' }}
        minWidth={{ lg: 210 }}
        maxWidth={{ lg: 290 }}
        overflow='auto'
      >
        <List sx={{ ...listContainerStyle, px: 1 }} aria-label='My View dashboards'>
          {dashboards.length === 0 ? (
            <Stack alignItems='center' justifyContent='center' py={4} gap={1} minWidth={200}>
              <InboxOutlinedIcon sx={{ fontSize: 36, color: '#c0c0c0' }} />
              <Typography variant='body2' color='text.secondary'>
                No dashboards in this solution
              </Typography>
            </Stack>
          ) : (
            dashboards.map((dashboardItem) => (
              <ListItem disablePadding key={dashboardItem.id} sx={{ mb: 1 }}>
                <ListItemButton
                  selected={activeDashboard?.id === dashboardItem.id}
                  onClick={() => {
                    onDashboardClick(dashboardItem);
                  }}
                  onMouseEnter={() => handleOnPrefetchMyViewItem(dashboardItem)}
                  sx={{
                    ...menuItemStyle,
                    paddingLeft: `10px`,
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  <Typography variant='input-label' sx={menuItemTextStyle}>
                    {dashboardItem.dashboardName}
                  </Typography>
                  <KeyboardArrowRight
                    sx={{
                      marginLeft: 'auto',
                      color: activeDashboard?.id === dashboardItem.id ? '#3f50b5' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Stack>
      <Divider
        orientation='vertical'
        variant='middle'
        flexItem
        sx={{ display: { xs: 'none', lg: 'block' } }}
      />
      <Divider sx={{ display: { xs: 'block', lg: 'none' } }} />
      <Stack flex={1} minWidth={0}>
        {activeDashboard && (
          <MyViewDashboardPreview
            dashboard={activeDashboard}
            onItemClick={handleOnMyViewItemClick}
            onItemPrefetch={handleOnMyViewItemPrefetch}
          />
        )}
      </Stack>
    </>
  );

  const renderComponentDefinitionDashboards = (view: ComponentDefinitionView) => {
    const dashboards: MyViewDashboardItem[] = mapSolutionWithDashboard.get(view.componentDefinitionId) ?? [];

    return (
      <Box
        component='section'
        aria-label='Component Definition dashboards'
        sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #E2E6EC' }}
      >
        {view.componentDefinitionName && (
          <Typography
            component='h2'
            sx={{ m: 0, mb: 1.5, color: '#19345D', fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.3 }}
          >
            {view.componentDefinitionName}
          </Typography>
        )}
        {dashboards.length ? (
          <Stack direction={{ xs: 'column', lg: 'row' }} gap={1.5} minHeight={360}>
            {renderDashboardColumns(dashboards, view.selectedDashboard, handleOnComponentDefinitionDashboardClick)}
          </Stack>
        ) : (
          <Stack alignItems='center' justifyContent='center' py={6} gap={1}>
            <InboxOutlinedIcon sx={{ fontSize: 48, color: '#c0c0c0' }} />
            <Typography variant='body2' color='text.secondary'>
              You have no My View dashboards in this Component Definition
            </Typography>
          </Stack>
        )}
      </Box>
    );
  };

  // Planning and Data Insight show their Rowena dialog when it has content,
  // and a coming soon placeholder otherwise.
  const renderRowenaSection = (section: HomeSection, content: string | null | undefined, placeholder: ReactNode) => {
    if (isLoading) return null;
    if (!hasRowenaText(content)) return placeholder;

    return (
      <>
        <PersonalUpdateCard
          userName={profile.name}
          welcomeContent={content}
          onExplore={handleWelcomeExplore}
          onKpiSelect={handleWelcomeKpiSelect}
          onAccountabilitySelect={handleWelcomeAccountabilitySelect}
          onComponentDefinitionSelect={handleWelcomeComponentDefinitionSelect}
        />
        {componentDefinitionView?.section === section && renderComponentDefinitionDashboards(componentDefinitionView)}
      </>
    );
  };

  return (
    <TenantHomeDashboardNavigationBar
      breadCrumbs={[]}
      containerSx={{ height: '100%', flex: 1, flexDirection: 'column', overflowY: 'auto', display: 'flex' }}
      rootContainerSx={{ height: '90vh' }}
    >
      <Grid container spacing={2} height='100%' sx={{ flexDirection: 'column', flexWrap: 'nowrap' }}>
        <Grid
          item
          sx={{ width: '100%', flex: shouldShowMyViewOptions ? '1 1 auto' : '0 0 auto', minHeight: 0 }}
        >
          <Box
            id='my-view'
            sx={{
              height: shouldShowMyViewOptions ? '100%' : 'auto',
            }}
          >
            <Box p={2}>
              <MyViewHeader />
              {hasWelcomeMessage && <PersonalUpdateHeader />}
              <HomeSectionTabs value={activeHomeSection} onChange={setActiveHomeSection} />
              <HomeSectionPanel section='tracking' activeSection={activeHomeSection}>
                {hasWelcomeMessage && (
                  <PersonalUpdateCard
                    userName={profile.name}
                    welcomeContent={welcomeContent}
                    welcomeMessage={welcomeMessage}
                    onExplore={handleWelcomeExplore}
                    onKpiSelect={handleWelcomeKpiSelect}
                    onAccountabilitySelect={handleWelcomeAccountabilitySelect}
                    onComponentDefinitionSelect={handleWelcomeComponentDefinitionSelect}
                  />
                )}
                {componentDefinitionView?.section === 'tracking' ? (
                  renderComponentDefinitionDashboards(componentDefinitionView)
                ) : (
                  <>
                    {!isMyViewVisible && !selectedAccountabilityId && (
                      <KPIDisplay selectedKpiId={selectedKpiId} onKpiSelect={setSelectedKpiId} />
                    )}
                    {selectedAccountabilityId && (
                      <DisplayAccouuntability accountabilityDefinitionId={selectedAccountabilityId} />
                    )}
                  </>
                )}
                <Divider sx={{ my: 1.5 }} />
                {shouldShowMyViewOptions && (
                <Stack id='my-view-options' height='100%'>
                  {isLoading ? (
                    <Stack width={'20%'} sx={listContainerStyle}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton
                          key={i}
                          animation='wave'
                          variant='rounded'
                          height={32}
                          sx={{ mb: 1, borderRadius: '4px' }}
                        />
                      ))}
                    </Stack>
                  ) : !allMyViewsData?.data.solutions.length ? (
                    <Stack alignItems='center' justifyContent='center' py={6} gap={1}>
                      <InboxOutlinedIcon sx={{ fontSize: 48, color: '#c0c0c0' }} />
                      <Typography variant='body2' color='text.secondary'>
                        No solutions configured
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction={{ xs: 'column', lg: 'row' }} gap={1.5} minHeight={360}>
                      <Stack
                        width={{ xs: '100%', lg: '20%' }}
                        minWidth={{ lg: 190 }}
                        maxWidth={{ lg: 260 }}
                        overflow='auto'
                        height='100%'
                      >
                        <List sx={listContainerStyle}>
                          {allMyViewsData?.data.solutions.map((solution) => (
                            <ListItem disablePadding key={solution.value} sx={{ mb: 1 }}>
                              <ListItemButton
                                selected={activeSolution?.value === solution.value}
                                onMouseEnter={() => {
                                  handleOnSolutionSelect(solution);
                                }}
                                sx={{
                                  ...menuItemStyle,
                                  paddingLeft: `10px`,
                                  marginBottom: 0,
                                  paddingBottom: 0,
                                }}
                              >
                                <Typography variant='input-label' sx={menuItemTextStyle}>
                                  {solution.label}
                                </Typography>
                                <KeyboardArrowRight
                                  sx={{
                                    marginLeft: 'auto',
                                    color: activeSolution?.value === solution.value ? '#3f50b5' : 'inherit',
                                    transition: 'color 0.15s ease',
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Stack>
                      <Divider
                        orientation='vertical'
                        variant='middle'
                        flexItem
                        sx={{ display: { xs: 'none', lg: 'block' } }}
                      />
                      <Divider sx={{ display: { xs: 'block', lg: 'none' } }} />
                      {renderDashboardColumns(myViewDashboardList, selectedDashboard, handleOnDashboardClick)}
                    </Stack>
                  )}
                </Stack>
                )}
              </HomeSectionPanel>
              <HomeSectionPanel section='data-insight' activeSection={activeHomeSection}>
                {renderRowenaSection(
                  'data-insight',
                  dataInsightsContent,
                  <HomeSectionPlaceholder
                    icon={<InsightsOutlinedIcon />}
                    title='Data Insight is coming soon'
                    description='Insights drawn from your data will appear here.'
                  />,
                )}
              </HomeSectionPanel>
              <HomeSectionPanel section='planning' activeSection={activeHomeSection}>
                {renderRowenaSection(
                  'planning',
                  planningContent,
                  <HomeSectionPlaceholder
                    icon={<EventNoteOutlinedIcon />}
                    title='Planning is coming soon'
                    description='Your planning workspace will appear here.'
                  />,
                )}
              </HomeSectionPanel>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </TenantHomeDashboardNavigationBar>
  );
}
